/**
 * 課題管理の書き込みが失敗したときに、画面に出す1行を作る。
 *
 * 競合（CONFLICT）は「誰が・どの項目を・何に」変えたかを出す（BR-17）。業務ルール違反（BAD_REQUEST）と
 * 対象なし（NOT_FOUND）はサーバの文をそのまま出す。権限を外された（FORBIDDEN）ときは読み直しを促す。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の保存の失敗（他の人の先の変更・業務ルール違反・権限切れ・通信の失敗）を、利用者が次に何をすればよいか分かる1行にする
 * @graph-connects none
 */
import { IssueConflictDataSchema, type IssueFieldConflict } from '@cortex/domain-ac-mens-hub-api';
import { ORPCError } from '@orpc/client';

import { ISSUE_FIELD_LABEL_KEYS } from './issue-mgmt-format.js';
import { formatIssueFieldValue, type Translate } from './issue-mgmt-values.js';

/**
 * 競合1つを文にする。元に戻すの競合は「K-001:title」の形なので、後ろの項目名で引く。
 * @graph-connects ISSUE_FIELD_LABEL_KEYS [references] 項目名の文言キー
 * @graph-connects formatIssueFieldValue [calls] 今の値の文言
 */
function describeConflict(
  conflict: IssueFieldConflict,
  t: Translate,
  nameOf: (email: string) => string,
): string {
  const field = conflict.field.slice(conflict.field.indexOf(':') + 1);
  const labelKey = ISSUE_FIELD_LABEL_KEYS[field];
  const params = {
    name: conflict.changedBy === null ? t('issueMgmt.error.someone') : nameOf(conflict.changedBy),
    field: labelKey === undefined ? t('issueMgmt.error.someField') : t(labelKey),
    value: formatIssueFieldValue(conflict.currentValue, field, t, nameOf),
  };
  return t('issueMgmt.error.conflict', params);
}

/**
 * 書き込みの失敗を1行にする。
 * @param nameOf メールから表示名を引く（名簿に無ければメール）
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の保存の失敗を、競合なら誰が何に変えたか、業務ルール違反ならその理由、それ以外は再試行を促す1行にする
 * @graph-connects describeConflict [calls] 競合の文
 */
export function describeIssueMgmtError(
  error: unknown,
  t: Translate,
  nameOf: (email: string) => string,
): string {
  if (!(error instanceof ORPCError)) return t('issueMgmt.error.network');
  switch (error.code) {
    case 'CONFLICT': {
      const parsed = IssueConflictDataSchema.safeParse(error.data);
      const first = parsed.success ? parsed.data.conflicts[0] : undefined;
      return first === undefined
        ? t('issueMgmt.error.conflictUnknown')
        : describeConflict(first, t, nameOf);
    }
    case 'BAD_REQUEST':
    case 'NOT_FOUND':
      return error.message;
    case 'FORBIDDEN':
      return t('issueMgmt.error.forbidden');
    default:
      return t('issueMgmt.error.network');
  }
}
